import { readBrowserStorage, writeBrowserStorage } from "./browserStorage";
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  addClientDeckComment,
  approveAsset,
  approveBrief,
  approveClientDeckItem,
  assignDesigner,
  attachDesignerAsset,
  createInitialContentWorkflowState,
  draftBriefForPiece,
  generatePlaceholderAsset,
  getAssetsForPiece,
  getActiveDeckVersion,
  getBriefForPiece,
  getClientDeckItems,
  getDeckVersionByReviewToken,
  getDeckBlockedCount,
  markBriefReady,
  requestClientChanges,
  scheduleClientItem,
  reviseBriefForPiece,
  saveBriefEdit,
  sendClientDeckVersion,
  submitAssetForReview,
  type ContentWorkflowState,
} from "./contentWorkflow";

type ContentWorkflowContextValue = {
  state: ContentWorkflowState;
  actions: {
    draftBrief: (pieceId: string) => void;
    reviseBrief: (pieceId: string, revisionRequest: string) => void;
    saveBrief: (pieceId: string, body: string) => void;
    markReady: (pieceId: string) => void;
    approveBrief: (pieceId: string) => void;
    assignDesigner: (pieceId: string, designer: string) => void;
    attachUpload: (pieceId: string, file: Pick<File, "name" | "type" | "size">) => void;
    generatePlaceholder: (pieceId: string) => void;
    submitAsset: (assetId: string) => void;
    approveAsset: (assetId: string) => void;
    approveClientItem: (pieceId: string, deckVersionId?: string) => void;
    requestClientChanges: (pieceId: string, deckVersionId?: string) => void;
    scheduleClientItem: (pieceId: string, deckVersionId?: string) => void;
    sendClientDeck: () => void;
    addDeckComment: (deckVersionId: string, pieceId: string | undefined, body: string) => void;
  };
  selectors: {
    getBriefForPiece: typeof getBriefForPiece;
    getAssetsForPiece: typeof getAssetsForPiece;
    getClientDeckItems: typeof getClientDeckItems;
    getDeckBlockedCount: typeof getDeckBlockedCount;
    getActiveDeckVersion: typeof getActiveDeckVersion;
    getDeckVersionByReviewToken: typeof getDeckVersionByReviewToken;
  };
};

const ContentWorkflowContext = createContext<ContentWorkflowContextValue | null>(null);
const STORAGE_KEY = "odc-content-workflow-state-v1";

export function ContentWorkflowProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ContentWorkflowState>(() => loadStoredWorkflowState());

  useEffect(() => {
    writeBrowserStorage("localStorage", STORAGE_KEY, JSON.stringify(state));
  }, [state]);

  const value = useMemo<ContentWorkflowContextValue>(
    () => ({
      state,
      actions: {
        draftBrief: (pieceId) => setState((current) => draftBriefForPiece(current, pieceId)),
        reviseBrief: (pieceId, revisionRequest) =>
          setState((current) => reviseBriefForPiece(current, pieceId, revisionRequest)),
        saveBrief: (pieceId, body) => setState((current) => saveBriefEdit(current, pieceId, body)),
        markReady: (pieceId) => setState((current) => markBriefReady(current, pieceId)),
        approveBrief: (pieceId) => setState((current) => approveBrief(current, pieceId)),
        assignDesigner: (pieceId, designer) => setState((current) => assignDesigner(current, pieceId, designer)),
        attachUpload: (pieceId, file) => setState((current) => attachDesignerAsset(current, pieceId, file)),
        generatePlaceholder: (pieceId) => setState((current) => generatePlaceholderAsset(current, pieceId)),
        submitAsset: (assetId) => setState((current) => submitAssetForReview(current, assetId)),
        approveAsset: (assetId) => setState((current) => approveAsset(current, assetId)),
        approveClientItem: (pieceId, deckVersionId) =>
          setState((current) => approveClientDeckItem(current, pieceId, deckVersionId)),
        requestClientChanges: (pieceId, deckVersionId) =>
          setState((current) => requestClientChanges(current, pieceId, deckVersionId)),
        scheduleClientItem: (pieceId, deckVersionId) =>
          setState((current) => scheduleClientItem(current, pieceId, deckVersionId)),
        sendClientDeck: () => setState((current) => sendClientDeckVersion(current)),
        addDeckComment: (deckVersionId, pieceId, body) =>
          setState((current) => addClientDeckComment(current, { deckVersionId, pieceId, body })),
      },
      selectors: {
        getBriefForPiece,
        getAssetsForPiece,
        getClientDeckItems,
        getDeckBlockedCount,
        getActiveDeckVersion,
        getDeckVersionByReviewToken,
      },
    }),
    [state],
  );

  return <ContentWorkflowContext.Provider value={value}>{children}</ContentWorkflowContext.Provider>;
}

export function useContentWorkflow() {
  const context = useContext(ContentWorkflowContext);
  if (!context) {
    throw new Error("useContentWorkflow must be used inside ContentWorkflowProvider.");
  }
  return context;
}

function loadStoredWorkflowState(): ContentWorkflowState {
  try {
    const stored = readBrowserStorage("localStorage", STORAGE_KEY);
    if (!stored) {
      return createInitialContentWorkflowState();
    }
    const parsed = JSON.parse(stored) as Partial<ContentWorkflowState>;
    if (!Array.isArray(parsed.pieces) || !Array.isArray(parsed.briefs) || !Array.isArray(parsed.assets)) {
      return createInitialContentWorkflowState();
    }
    return {
      ...createInitialContentWorkflowState(),
      ...parsed,
      deckVersions: Array.isArray(parsed.deckVersions) ? parsed.deckVersions : [],
      deckComments: Array.isArray(parsed.deckComments) ? parsed.deckComments : [],
    };
  } catch {
    return createInitialContentWorkflowState();
  }
}
